import { t } from 'i18next';
import { flux } from '@/core/flux.module';
import { getItemFinder } from '@/itemFinder/itemFinder.actions';
import { isStoredTableSearch } from '@/itemFinder/itemFinder.utilities';
import { getTableDefinitionStore, getTableEditorStore } from '@/tableDefinitionEditor/store.utilities';
import { CONTROL_PANE_DISPLAY, EDITOR_TYPES } from '@/tableDefinitionEditor/tableDefinition.constants';
import {
  setShowChildLimitExceededWarning,
  setShowStoredTableSearchWarning,
} from '@/tableDefinitionEditor/tableDefinitionEditor.actions';
import { fetchInputFeaturesFromTableApplication } from '@/tools/models/template.utilities';
import { errorToast, successToast } from '@/utilities/toast.utilities';
import { fetchItemTables, fetchScalingTables } from '@/workbook/workbook.actions';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import { sqTableDefinitionsApi } from '@seeqdev/api/endpoint/TableDefinitionsApi';
import { sqTemplatesApi } from '@seeqdev/api/endpoint/TemplatesApi';
import { SeeqNames } from '@seeqdev/constants';
import type { GridApi } from 'ag-grid-community';
import type {
  ColumnDefinition,
  EditorType,
  MaterializedTableItemColumnPropertyPair,
  MaterializedTableOutput,
  ModelToSelect,
  StoredTableSearchFailure,
  StoredTableSearchFailures,
} from '@/tableDefinitionEditor/tableDefinition.types';
import type { DerivedFeatureV1 } from '@seeqdev/api/model/DerivedFeatureV1';
import type { HierarchyOutputV1 } from '@seeqdev/api/model/HierarchyOutputV1';
import type { ItemFinderOutputV1 } from '@seeqdev/api/model/ItemFinderOutputV1';
import type { ItemSearchPreviewV1 } from '@seeqdev/api/model/ItemSearchPreviewV1';
import type { SearchTypeEnum } from '@seeqdev/api/model/PropertySearchV1';
import type { RowRulesResultV1 } from '@seeqdev/api/model/RowRulesResultV1';
import type { BatchActionEnum } from '@seeqdev/api/model/TableDefinitionInputV1';
import type { TableDefinitionOutputV1 } from '@seeqdev/api/model/TableDefinitionOutputV1';
import type { TemplateTableApplicationsOutputListV1 } from '@seeqdev/api/model/TemplateTableApplicationsOutputListV1';

export function setSubscriptionId(editorType: EditorType, id: string | undefined) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_SUBSCRIBER_ID`, { subscriberId: id });
}

export function setShouldSearchForItems(editorType: EditorType, shouldSearchForItems: boolean) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_SHOULD_SEARCH_FOR_ITEMS`, { shouldSearchForItems });
}

export function setName(editorType: EditorType, name: string) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_NAME`, { name });
}

export function setSearchTypeAI(editorType: EditorType, searchTypeAI: SearchTypeEnum | undefined) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_SEARCH_TYPE_AI`, { searchTypeAI });
}

export function setDescription(editorType: EditorType, description: string) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_DESCRIPTION`, { description });
}

export function setBatchAction(editorType: EditorType, batchAction: BatchActionEnum) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_BATCH_ACTION`, { batchAction });
}

export function setItemFinder(editorType: EditorType, itemFinder: ItemFinderOutputV1 | undefined) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_ITEM_FINDER`, { itemFinder });
}

export function setTableDefinition(editorType: EditorType, output: TableDefinitionOutputV1) {
  const store = getTableDefinitionStore(editorType);
  const currentTableDefinition = store.tableDefinitionOutput;
  const preservedItemFinder = output.itemFinder ?? currentTableDefinition?.itemFinder;
  // Preserve itemFinder if it exists in store but not in new output, but do not
  // add an explicit `itemFinder: undefined` property when neither has a value.
  const mergedOutput: TableDefinitionOutputV1 =
    preservedItemFinder !== undefined
      ? {
          ...output,
          itemFinder: preservedItemFinder,
        }
      : output;
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_TABLE_DEFINITION`, { tableDefinition: mergedOutput });
}

export function resetTableDefinition(editorType: EditorType) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_RESET`);
}

export function setMaterializedTable(editorType: EditorType, materializedTable: MaterializedTableOutput | undefined) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_MATERIALIZED_TABLE`, { materializedTable });
}

export async function fetchStoredTableWarnings(editorType: EditorType, itemFinder?: ItemFinderOutputV1) {
  if (itemFinder && itemFinder.searches.some((search) => isStoredTableSearch(search))) {
    const {
      data: { storedTableSearchFailures },
    } = await sqItemsApi.getStoredTableSearchFailures({ id: itemFinder.id });
    if (storedTableSearchFailures.length > 0) {
      // Group failures by storedTableId, then by rowId, and translate messages
      const groupedFailures = storedTableSearchFailures.reduce((acc, failure) => {
        const { storedTableId, rowId, messageKey, searchString } = failure;

        let tableGroup = acc.find((group) => group.storedTableId === storedTableId);
        if (!tableGroup) {
          tableGroup = { storedTableId, failures: [] };
          acc.push(tableGroup);
        }

        let rowFailure = tableGroup.failures.find((f: StoredTableSearchFailure) => f.rowId === rowId);
        if (!rowFailure) {
          rowFailure = { rowId, messages: [] };
          tableGroup.failures.push(rowFailure);
        }

        // Translate the message with searchString interpolation
        const translatedMessage = t(messageKey, { searchString });
        rowFailure.messages.push(translatedMessage);

        return acc;
      }, [] as StoredTableSearchFailures[]);

      setStoredTableFailures(editorType, groupedFailures);

      await getStoredTables(
        editorType,
        groupedFailures.map((groupedFailure) => groupedFailure.storedTableId),
      );

      setShowStoredTableSearchWarning(editorType, true);
    } else {
      setShowStoredTableSearchWarning(editorType, false);
      setStoredTableFailures(editorType, []);
      setStoredTables(editorType, []);
    }
  } else {
    setShowStoredTableSearchWarning(editorType, false);
    setStoredTableFailures(editorType, []);
    setStoredTables(editorType, []);
  }
  if (itemFinder?.lastRunWarnings?.includes('ITEM_FINDER.CHILD_LIMIT_EXCEEDED')) {
    setShowChildLimitExceededWarning(editorType, true);
  } else {
    setShowChildLimitExceededWarning(editorType, false);
  }
}

const getStoredTables = async (editorType: EditorType, storedTableIds: string[]) => {
  try {
    const { data } = await sqItemsApi.searchItems({
      types: [SeeqNames.Types.StoredTable],
      filters: storedTableIds.map((id) => `${SeeqNames.Properties.Id}==${id}`),
      limit: 1000,
    });

    setStoredTables(editorType, data.items ?? []);
  } catch (error) {
    errorToast({ httpResponseOrError: error });
  }
};

export function setDoTableReload(editorType: EditorType, doTableReload: boolean) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_DO_TABLE_RELOAD`, { doTableReload });
}

export function setDisplayPropertyForColumn(
  editorType: EditorType,
  uuidColumnToPropertyNamePair: MaterializedTableItemColumnPropertyPair,
) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_PROPERTY_NAME_FOR_UUID_COLUMN`, {
    uuidColumnToPropertyNamePair,
  });
}

export function addPendingTableId(editorType: EditorType, pendingTableId: string) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_ADD_PENDING_TABLE_ID`, { pendingTableId });
}

export function removePendingTableId(editorType: EditorType, pendingTableId: string) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_REMOVE_PENDING_TABLE_ID`, { pendingTableId });
}

export function setEvaluationResults(editorType: EditorType, evaluationResults: { [key: string]: RowRulesResultV1[] }) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_EVALUATION_RESULTS`, { evaluationResults });
}

export function mergeEvaluationResults(
  editorType: EditorType,
  evaluationResults: { [key: string]: RowRulesResultV1[] },
) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_MERGE_EVALUATION_RESULTS`, { evaluationResults });
}

export function setCreatedItemFinderId(editorType: EditorType, id: string | undefined) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_CREATED_ITEM_FINDER_ID`, { createdItemFinderId: id });
}

export function setAllowHiddenColumnsAsInputs(editorType: EditorType, allowHiddenColumnsAsInputs: boolean) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_ALLOW_HIDDEN_COLUMNS_AS_INPUTS`, {
    allowHiddenColumnsAsInputs,
  });
}

export function setAgGridApi(editorType: EditorType, agGridApi: GridApi) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_AG_GRID_API`, { agGridApi });
}

export function setSelectedColumns(editorType: EditorType, selectedColumns: string[]) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_SELECTED_COLUMNS`, { selectedColumns });
}

export function setRowDataIsLoading(editorType: EditorType, rowDataIsLoading: boolean) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_ROW_DATA_IS_LOADING`, { rowDataIsLoading });
}

export function setIsLoadingDefinition(editorType: EditorType, isLoadingDefinition: boolean) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_IS_LOADING_DEFINITION`, { isLoadingDefinition });
}

export function setScrollToColumnId(editorType: EditorType, scrollToColumnId: string | undefined) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_SCROLL_TO_COLUMN_ID`, { scrollToColumnId });
}

export function setIsApplyingTemplate(editorType: EditorType, isApplyingTemplate: boolean) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_IS_APPLYING_TEMPLATE`, { isApplyingTemplate });
  if (isApplyingTemplate) {
    flux.dispatch(`${editorType}_SET_CONTROL_PANE`, { controlPane: CONTROL_PANE_DISPLAY.TEMPLATE_CONTROL });
  }
}

export function setTemplateInputToSelect(editorType: EditorType, templateInputToSelect: ModelToSelect | undefined) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_TEMPLATE_INPUT_TO_SELECT`, { templateInputToSelect });
}

export function setColumnIdToBeUsedAsTemplateInput(
  editorType: EditorType,
  columnIdToBeUsedAsTemplateInput: string | undefined,
) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_COLUMN_ID_TO_BE_USED_AS_TEMPLATE_INPUT`, {
    columnIdToBeUsedAsTemplateInput,
  });
}

export function setTemplateColumns(
  editorType: EditorType,
  templateColumns: TemplateTableApplicationsOutputListV1 | undefined,
) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_TEMPLATE_COLUMNS`, { templateColumns });
}

export function setInputFeatureColumnIds(editorType: EditorType, inputFeatureColumnIds: Record<string, string[]>) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_INPUT_FEATURE_COLUMN_IDS`, { inputFeatureColumnIds });
}

export function setRequestProgress(
  editorType: EditorType,
  payload: {
    id: string;
    progress: Record<string, unknown>;
    timingInformation?: Record<string, unknown>;
    meterInformation?: Record<string, unknown>;
  },
) {
  flux.dispatch(`REQUEST_SET_PROGRESS`, payload);
}

export function addRowItemIdsInsertedInGroupLevel(editorType: EditorType, ids: string[]) {
  flux.dispatch(`ITEM_ADD_ROWS_TO_FILTER_OUT`, { ids });
}

export function clearRowItemIdsInsertedInGroupLevel(editorType: EditorType) {
  flux.dispatch('ITEM_CLEAR_ROW_IDS');
}

export function setShowCreateTemplate(editorType: EditorType, show: boolean) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_SHOW_CREATE_TEMPLATE`, { show });
  if (show) {
    flux.dispatch(`${editorType}_SET_CONTROL_PANE`, { controlPane: CONTROL_PANE_DISPLAY.COLUMN_CONTROL });
  }
}

export function setIsEditingTemplate(editorType: EditorType, isEditingTemplate: boolean) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_IS_EDITING_TEMPLATE`, { isEditingTemplate });
}

export function getEditTemplateRemovedOutputColumnIds(editorType: EditorType): string[] {
  return Object.keys(getTableDefinitionStore(editorType).editTemplateRemovedOutputByColumnId ?? {});
}

export function findRemovedOutputSnapshotKey(
  columnId: string,
  removedOutputByColumnId: Record<string, DerivedFeatureV1>,
): string | undefined {
  return Object.keys(removedOutputByColumnId).find((key) => key.toLowerCase() === columnId.toLowerCase());
}

export function removeEditTemplateRemovedOutputColumnId(editorType: EditorType, columnId: string) {
  const store = getTableDefinitionStore(editorType);
  const snapshots = store.editTemplateRemovedOutputByColumnId ?? {};
  const keyToRemove = findRemovedOutputSnapshotKey(columnId, snapshots);
  if (!keyToRemove) {
    return;
  }
  const { [keyToRemove]: _removed, ...remainingSnapshots } = snapshots;
  setEditTemplateRemovedOutputByColumnId(editorType, remainingSnapshots);
}

export function setEditTemplateRemovedOutputByColumnId(
  editorType: EditorType,
  editTemplateRemovedOutputByColumnId: Record<string, DerivedFeatureV1>,
) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_EDIT_TEMPLATE_REMOVED_OUTPUT_BY_COLUMN_ID`, {
    editTemplateRemovedOutputByColumnId,
  });
}

export function addEditTemplateRemovedOutput(editorType: EditorType, columnId: string, feature: DerivedFeatureV1) {
  const store = getTableDefinitionStore(editorType);
  setEditTemplateRemovedOutputByColumnId(editorType, {
    ...store.editTemplateRemovedOutputByColumnId,
    [columnId]: feature,
  });
}

export function setColumns(editorType: EditorType, columns: ColumnDefinition[]) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_COLUMNS`, { columns });
}

export function setHierarchiesUsingThisColumnAsGroup(editorType: EditorType, hierarchies: HierarchyOutputV1[]) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_HIERARCHIES_USING_COLUMN`, { hierarchies });
}

export function setColumnToDelete(editorType: EditorType, column: ColumnDefinition | undefined) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_COLUMN_TO_DELETE`, { column });
}

export function setShowRetireTemplateModal(editorType: EditorType, show: boolean) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_SHOW_RETIRE_TEMPLATE_MODAL`, { show });
}

export function setStoredTableFailures(
  editorType: EditorType,
  storedTableFailures: StoredTableSearchFailures[] | undefined,
) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_STORED_TABLE_FAILURES`, { storedTableFailures });
}

export function setStoredTables(editorType: EditorType, storedTables: ItemSearchPreviewV1[] | undefined) {
  flux.dispatch(`${editorType}_TABLE_DEFINITION_SET_STORED_TABLES`, { storedTables });
}

export async function fetchTableDefinitionItemFinderAndMetadata(
  editorType: EditorType,
  subscriberId: string,
  itemFinder?: ItemFinderOutputV1,
) {
  const foundItemFinder = itemFinder ?? (await getItemFinder(subscriberId));
  await fetchStoredTableWarnings(editorType, foundItemFinder);
  setItemFinder(editorType, foundItemFinder);
}

export async function initializeMaterializedTableEditors() {
  await initializeMaterializedTableEditor(EDITOR_TYPES.ITEM);
  await initializeMaterializedTableEditor(EDITOR_TYPES.SCALING);
}

/**
 * Fetch an existing table definition and its associated metadata and set it all in the store
 */
export async function getExistingTableDefinition(tableDefinitionId: string, editorType: EditorType) {
  setIsLoadingDefinition(editorType, true);
  try {
    const { data: tableDefinition } = await sqTableDefinitionsApi.getTableDefinition({ id: tableDefinitionId });

    await getTableTemplateApplications(tableDefinitionId, editorType);
    if (tableDefinition.subscription?.id) {
      await fetchTableDefinitionItemFinderAndMetadata(
        editorType,
        tableDefinition.subscription.id,
        tableDefinition.itemFinder,
      );
    }
    // set after fetching and setting metadata, as this setter triggers a table reload
    setTableDefinition(editorType, tableDefinition);
    return tableDefinition;
  } catch (e) {
    if (editorType === 'ITEM') {
      throw e;
    } else {
      errorToast({ httpResponseOrError: e });
    }
  } finally {
    setIsLoadingDefinition(editorType, false);
  }
}

export async function getTableTemplateApplications(tableDefinitionId: string, editorType: EditorType) {
  try {
    const { data: modelData } = await sqTemplatesApi.getTableDefinitionTemplateApplications({
      id: tableDefinitionId,
    });

    const fetchInputFeatures = async () => {
      const ids = await fetchInputFeaturesFromTableApplication(modelData);
      setInputFeatureColumnIds(editorType, ids);
    };

    if (modelData.applications?.length) {
      setTemplateColumns(editorType, modelData);
      fetchInputFeatures();
    } else {
      setTemplateColumns(editorType, undefined);
    }
  } catch (e) {
    errorToast({ httpResponseOrError: e });
  }
}

async function initializeMaterializedTableEditor(editorType: EditorType) {
  const editorStore = getTableEditorStore(editorType);
  const tableStore = getTableDefinitionStore(editorType);
  const tableId = editorStore.tableId;
  if (!!tableId && tableStore.id !== tableId) {
    return getExistingTableDefinition(tableId, editorType);
  }
  return undefined;
}

export async function duplicateTableDefinition(
  tableDefinition: { id: string; name: string },
  scopedTo: string,
  editorType: EditorType,
) {
  const duplicator: { [key in EditorType]: () => Promise<TableDefinitionOutputV1> } = {
    [EDITOR_TYPES.SCALING]: () =>
      sqTableDefinitionsApi
        .duplicateTableDefinition(
          {
            name: t('SCALING.TABLE.DUPLICATED_NAME', { originalName: tableDefinition.name }),
            scopedTo,
          },
          { id: tableDefinition.id },
        )
        .then(async ({ data }) => {
          await fetchScalingTables([scopedTo]);
          successToast({
            messageKey: 'SCALING.TABLE.DUPLICATE_SUCCESS',
            messageParams: { originalName: tableDefinition.name, duplicatedName: data.name },
          });
          return data;
        }),
    [EDITOR_TYPES.ITEM]: () =>
      sqTableDefinitionsApi
        .duplicateTableDefinition(
          {
            name: t('ITEM_TABLE.DUPLICATED_NAME', { originalName: tableDefinition.name }),
            scopedTo,
          },
          { id: tableDefinition.id },
        )
        .then(async ({ data }) => {
          await fetchItemTables([scopedTo]);
          successToast({
            messageKey: 'ITEM_TABLE.DUPLICATE_SUCCESS',
            messageParams: { originalName: tableDefinition.name, duplicatedName: data.name },
          });
          return data;
        }),
  };

  try {
    return await duplicator[editorType]();
  } catch (e) {
    errorToast({ httpResponseOrError: e });
  }
}
